import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/5 py-8 md:py-12 lg:py-16 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-6 mix-blend-overlay"></div>
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground mb-4 leading-tight">
              Own Your Online Store in Less Than 3 Minutes
            </h1>

            <p className="font-body text-base md:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto lg:mx-0">
              Cultaz Cloud and the Offline POS are engineered for speed - the Cloud requires
              internet to sync with your online store, while the Offline POS runs fully without
              internet. Offline can connect to other systems on the same local network so multiple
              cashiers can work simultaneously.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <a href="https://app.cultaz.com" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button variant="default" size="lg" iconName="Rocket" iconPosition="left">
                  Get Online (under 20 seconds)
                </Button>
              </a>
              <Link to="/downloads" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" iconName="Download" iconPosition="left">
                  Download Desktop POS
                </Button>
              </Link>
            </div>

            <div className="mt-3 text-sm text-muted-foreground max-w-2xl mx-auto lg:mx-0">
              <strong className="text-foreground">Cultaz Cloud</strong>: forever free on a subdomain - set up your online store with a free Cultaz subdomain and sync when you choose to upgrade.
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto lg:mx-0">
              <motion.div whileHover={{ y: -4 }} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">✔</div>
                <div>
                  <div className="font-body text-sm font-semibold">Sell Offline</div>
                  <div className="font-body text-xs text-muted-foreground">POS, receipts & local reports</div>
                </div>
              </motion.div>
              <motion.div whileHover={{ y: -4 }} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">⚡</div>
                <div>
                  <div className="font-body text-sm font-semibold">Fast Setup</div>
                  <div className="font-body text-xs text-muted-foreground">Online store in minutes</div>
                </div>
              </motion.div>
              <motion.div whileHover={{ y: -4 }} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold">🔒</div>
                <div>
                  <div className="font-body text-sm font-semibold">Local & Secure</div>
                  <div className="font-body text-xs text-muted-foreground">Keep records on-device</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative">

            {/* Simple rotating hero images. Add screenshots to /public/assets/images with these names. */}
            <HeroCarousel />

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 left-6 bg-card border border-border rounded-xl p-3 md:p-4 max-w-xs shadow-md">

              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary font-semibold">✓</div>
                <div className="font-body text-sm font-semibold">Offline POS</div>
              </div>
              <p className="font-body text-xs text-muted-foreground">Keep sales & inventory locally on your device.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="absolute -top-6 right-6 bg-card border border-border rounded-xl p-3 md:p-4 max-w-xs shadow-md">

              <div className="flex items-center gap-3 mb-1">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary font-semibold">★</div>
                <div className="font-body text-sm font-semibold">Quick Setup</div>
              </div>
              <p className="font-body text-xs text-muted-foreground">Create your online catalog and start selling quickly.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>);


};

export default HeroSection;

function HeroCarousel() {
  const images = [
    { src: '/cultaz-cloud.png', alt: 'Cultaz Cloud POS on laptop' },
    { src: '/cloud-app-screenshot/dashboard-light.png', alt: 'Cultaz Cloud Dashboard' },
    { src: '/cloud-app-screenshot/analytics-light.png', alt: 'Cultaz Analytics' }
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border">
      <img src={images[idx].src} alt={images[idx].alt} className="w-full h-auto aspect-[4/3] object-cover" />
      <div className="absolute bottom-3 left-3 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Show image ${i + 1}`}
            className={`h-2 w-8 rounded-full ${i === idx ? 'bg-primary' : 'bg-muted/40'}`}
          />
        ))}
      </div>
    </div>
  );
}